import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  Button,
  TablePagination,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  MonetizationOn as MoneyIcon,
  ShoppingCart as CartIcon,
  Store as StoreIcon,
  PictureAsPdf as InvoiceIcon,
  FileDownload as ExportIcon,
} from "@mui/icons-material";
import { useQuery } from "@apollo/client";
import { GET_COUPON_PROGRESS, GET_COUPON_CLAIMS } from "../graphql/queries";
import { API_URL } from "../constants";
import * as XLSX from "xlsx";
import ClaimsFilterPanel from "../Components/ClaimsFilterPanel";

const API_BASE = API_URL.replace(/\/graphql\/?$/i, "");
const INVOICE_BASE_URL = "https://api-leadgen.eventegration.com";

const CouponProgressReportPage = () => {
  const [progressData, setProgressData] = useState(null);

  const { data, loading } = useQuery(GET_COUPON_PROGRESS);
  const { data: claimsData } = useQuery(GET_COUPON_CLAIMS);

  useEffect(() => {
    if (data?.getCouponProgress) {
      try {
        const parsed = JSON.parse(data.getCouponProgress);
        setProgressData(parsed);
      } catch (error) {
        console.error("Error parsing progress data:", error);
      }
    }
  }, [data]);

  const getInvoiceFullUrl = (eventId, invoicePath) => {
    if (!invoicePath || !eventId) return "";
    const path = invoicePath.startsWith("http")
      ? invoicePath
      : `${INVOICE_BASE_URL}/${invoicePath.replace(/^\//, "")}`;
    return path;
  };

  const handleViewInvoice = (eventId, invoicePath) => {
    const url = getInvoiceFullUrl(eventId, invoicePath);
    if (url) window.open(url, "_blank");
  };

  const claims = claimsData?.getCouponClaims ?? [];

  const [claimFilterShopName, setClaimFilterShopName] = useState("");
  const [claimFilterShopLocation, setClaimFilterShopLocation] = useState("");
  const [claimFilterDateFrom, setClaimFilterDateFrom] = useState("");
  const [claimFilterDateTo, setClaimFilterDateTo] = useState("");

  const claimShopNames = React.useMemo(
    () => claims.map((c) => (c.shopName || "").trim()).filter(Boolean),
    [claims],
  );
  const claimShopLocations = React.useMemo(
    () => claims.map((c) => (c.shopLocation || "").trim()).filter(Boolean),
    [claims],
  );

  const filteredClaims = React.useMemo(() => {
    let list = claims;
    const shopNameQ = (claimFilterShopName || "").trim().toLowerCase();
    const shopLocQ = (claimFilterShopLocation || "").trim().toLowerCase();
    if (shopNameQ) {
      list = list.filter((c) =>
        (c.shopName || "").toLowerCase().includes(shopNameQ),
      );
    }
    if (shopLocQ) {
      list = list.filter((c) =>
        (c.shopLocation || "").toLowerCase().includes(shopLocQ),
      );
    }
    if (claimFilterDateFrom) {
      const fromStart = new Date(claimFilterDateFrom);
      fromStart.setHours(0, 0, 0, 0);
      list = list.filter(
        (c) => c.createdAt && new Date(c.createdAt) >= fromStart,
      );
    }
    if (claimFilterDateTo) {
      const toEnd = new Date(claimFilterDateTo);
      toEnd.setHours(23, 59, 59, 999);
      list = list.filter((c) => c.createdAt && new Date(c.createdAt) <= toEnd);
    }
    return list;
  }, [
    claims,
    claimFilterShopName,
    claimFilterShopLocation,
    claimFilterDateFrom,
    claimFilterDateTo,
  ]);

  const formatDate = (iso) => {
    if (!iso) return "—";
    try {
      const d = new Date(iso);
      return (
        d.toLocaleDateString() +
        " " +
        d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
    } catch {
      return iso;
    }
  };

  const shopWiseArray = Object.entries(progressData?.shopWiseData || {}).map(
    ([standId, data]) => ({
      standId,
      shopName: data.shopName || "Unknown",
      shopLocation: data.shopLocation ?? "",
      shopContact: data.shopContact ?? "",
      ...data,
    }),
  );

  const [shopPage, setShopPage] = useState(0);
  const [shopRowsPerPage, setShopRowsPerPage] = useState(10);

  const exportActiveShops = () => {
    const rows = shopWiseArray.map((s) => ({
      "Shop Name": s.shopName,
      "Shop Location": s.shopLocation || "",
      "Shop Contact": s.shopContact || "",
      Claims: s.claims,
      "Total Discount": parseFloat(s.totalDiscount || 0).toFixed(2),
      "Total Bills": parseFloat(s.totalBills || 0).toFixed(2),
      "Avg Discount per Claim":
        s.claims > 0
          ? (parseFloat(s.totalDiscount || 0) / s.claims).toFixed(2)
          : "0.00",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Active Shops");
    XLSX.writeFile(
      wb,
      `Coupon_Claim_Active_Shops_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };

  const exportClaims = () => {
    const rows = filteredClaims.map((c) => ({
      ID: c.id,
      "Coupon #": c.couponNo ?? "",
      "Total Bill":
        c.totalBill != null ? parseFloat(c.totalBill).toFixed(2) : "",
      "Discount Amount":
        c.discountApplied != null
          ? parseFloat(c.discountApplied).toFixed(2)
          : "",
      "Final Amount":
        c.grandTotal != null ? parseFloat(c.grandTotal).toFixed(2) : "",
      "Claimed by Shop": c.shopName ?? "",
      "Shop Location": c.shopLocation ?? "",
      "Shop Contact": c.shopContact ?? "",
      "Coupon claimed Date": formatDate(c.createdAt),
      "View invoice":
        c.invoicePath && c.eventId
          ? getInvoiceFullUrl(c.eventId, c.invoicePath)
          : "",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Coupon claims");
    XLSX.writeFile(
      wb,
      `Coupon_Claim_${new Date().toISOString().slice(0, 10)}.xlsx`,
    );
  };
  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading progress report...</Typography>
      </Box>
    );
  }

  if (!progressData) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography>No data available</Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Analytics and progress tracking for coupon claims
      </Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <CartIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography variant="h6">Total Coupon claims</Typography>
              </Box>
              <Typography variant="h4">
                {progressData.totalClaims || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <MoneyIcon sx={{ mr: 1, color: "success.main" }} />
                <Typography variant="h6">Total Discount</Typography>
              </Box>
              <Typography variant="h4">
                {parseFloat(progressData.totalDiscount || 0).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <TrendingUpIcon sx={{ mr: 1, color: "info.main" }} />
                <Typography variant="h6">Total Bills</Typography>
              </Box>
              <Typography variant="h4">
                {parseFloat(progressData.totalBills || 0).toFixed(2)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={1}>
                <StoreIcon sx={{ mr: 1, color: "warning.main" }} />
                <Typography variant="h6">Active Shops</Typography>
              </Box>
              <Typography variant="h4">{shopWiseArray.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">Active Shops</Typography>
          <Button
            startIcon={<ExportIcon />}
            variant="outlined"
            size="small"
            onClick={exportActiveShops}
          >
            Export
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Shop Name</TableCell>
                <TableCell>Shop Location</TableCell>
                <TableCell>Shop Contact</TableCell>
                <TableCell align="right">Claims</TableCell>
                <TableCell align="right">Total Discount</TableCell>
                <TableCell align="right">Total Bills</TableCell>
                <TableCell align="right">Avg Discount per Claim</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shopWiseArray.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No shop data available
                  </TableCell>
                </TableRow>
              ) : (
                shopWiseArray
                  .slice(
                    shopPage * shopRowsPerPage,
                    shopPage * shopRowsPerPage + shopRowsPerPage,
                  )
                  .map((shop, index) => (
                    <TableRow
                      key={
                        shop.key || shop.standId + "-" + shop.shopName || index
                      }
                    >
                      <TableCell>{shop.shopName}</TableCell>
                      <TableCell>{shop.shopLocation || "—"}</TableCell>
                      <TableCell>{shop.shopContact || "—"}</TableCell>
                      <TableCell align="right">
                        <Chip
                          label={shop.claims}
                          color="primary"
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(shop.totalDiscount || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {parseFloat(shop.totalBills || 0).toFixed(2)}
                      </TableCell>
                      <TableCell align="right">
                        {shop.claims > 0
                          ? (
                              parseFloat(shop.totalDiscount || 0) / shop.claims
                            ).toFixed(2)
                          : "0.00"}{" "}
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={shopWiseArray.length}
          page={shopPage}
          onPageChange={(_, p) => setShopPage(p)}
          rowsPerPage={shopRowsPerPage}
          onRowsPerPageChange={(e) => {
            setShopRowsPerPage(parseInt(e.target.value, 10));
            setShopPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <Paper sx={{ p: 3, mt: 3 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          sx={{ mb: 2 }}
        >
          <Typography variant="h6">Scanned Coupon Claim Data</Typography>
          <Button
            startIcon={<ExportIcon />}
            variant="outlined"
            size="small"
            onClick={exportClaims}
          >
            Export
          </Button>
        </Box>
        <ClaimsFilterPanel
          shopNames={claimShopNames}
          shopLocations={claimShopLocations}
          shopName={claimFilterShopName}
          shopLocation={claimFilterShopLocation}
          dateFrom={claimFilterDateFrom}
          dateTo={claimFilterDateTo}
          onShopNameChange={setClaimFilterShopName}
          onShopLocationChange={setClaimFilterShopLocation}
          onDateFromChange={setClaimFilterDateFrom}
          onDateToChange={setClaimFilterDateTo}
          onClear={() => {
            setClaimFilterShopName("");
            setClaimFilterShopLocation("");
            setClaimFilterDateFrom("");
            setClaimFilterDateTo("");
          }}
        />
        {filteredClaims.length < claims.length && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Showing {filteredClaims.length} of {claims.length} claims
          </Typography>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Coupon #</TableCell>
                <TableCell align="right">Total Bill</TableCell>
                <TableCell align="right">Discount Amount</TableCell>
                <TableCell align="right">Final Amount</TableCell>
                <TableCell>Claimed by Shop</TableCell>
                <TableCell>Shop Location</TableCell>
                <TableCell>Shop Contact</TableCell>
                <TableCell>Coupon claimed Date</TableCell>
                <TableCell>View invoice</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredClaims.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    {claims.length === 0
                      ? "No claims yet"
                      : "No claims match the current filters"}
                  </TableCell>
                </TableRow>
              ) : (
                filteredClaims.map((c) => (
                  <TableRow key={`${c.id}-${c.couponNo}-${c.createdAt}`}>
                    <TableCell>{c.id}</TableCell>
                    <TableCell>{c.scanName || "—"}</TableCell>
                    <TableCell>{c.couponNo || "—"}</TableCell>
                    <TableCell align="right">
                      {c.totalBill != null
                        ? parseFloat(c.totalBill).toFixed(2)
                        : "—"}{" "}
                    </TableCell>
                    <TableCell align="right">
                      {c.discountApplied != null
                        ? parseFloat(c.discountApplied).toFixed(2)
                        : "—"}{" "}
                    </TableCell>
                    <TableCell align="right">
                      {c.grandTotal != null
                        ? parseFloat(c.grandTotal).toFixed(2)
                        : "—"}{" "}
                    </TableCell>
                    <TableCell>{c.shopName || "—"}</TableCell>
                    <TableCell>{c.shopLocation || "—"}</TableCell>
                    <TableCell>{c.shopContact || "—"}</TableCell>
                    <TableCell>{formatDate(c.createdAt)}</TableCell>
                    <TableCell>
                      {c.invoicePath && c.eventId ? (
                        <Button
                          size="small"
                          startIcon={<InvoiceIcon />}
                          onClick={() =>
                            handleViewInvoice(c.eventId, c.invoicePath)
                          }
                        >
                          View
                        </Button>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default CouponProgressReportPage;
